import { Badge, Button } from '@/components/UI';
import FormCheckbox from '@/components/UI/FormCheckbox';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { AuditCandidate, OptimizerResultRow } from './types';
import { RAW_RETURN_SUM_LABEL } from './returnMetricCopy';
import {
    badgeVariantForAuditGrade,
    computeValidationStrength,
    formatDecimal,
    formatNullablePct01,
    formatPct01,
    formatSignedPct,
    profitColorClass,
    toNumber,
    validationBadgeVariant
} from './helpers';

export interface BuildResultColumnsParams {
    bundleSelectedResultIds: Set<number>;
    toggleBundleSelectedResultId: (resultId: number) => void;
    displayIndexByResultId: Map<number, number>;
    auditCandidateByResultId: Map<number, AuditCandidate>;
    auditPackPending: boolean;
    selectedJobUsesOptionEv: boolean;
    selectedJobUsesOptionMetric: boolean;
    selectedJobOutcomeMode: 'fixed_horizon' | 'strategy_exit' | undefined;
    replayConfirmationPendingResultId: number | null;
    openPromoteModal: (result: OptimizerResultRow) => void;
    onConfirmReplay: (result: OptimizerResultRow) => void;
    onShowRuleJson: (result: OptimizerResultRow) => void;
    onShowFolds: (resultId: number) => void;
}

export function buildResultColumns({
    bundleSelectedResultIds,
    toggleBundleSelectedResultId,
    displayIndexByResultId,
    auditCandidateByResultId,
    auditPackPending,
    selectedJobUsesOptionEv,
    selectedJobUsesOptionMetric,
    selectedJobOutcomeMode,
    replayConfirmationPendingResultId,
    openPromoteModal,
    onConfirmReplay,
    onShowRuleJson,
    onShowFolds
}: BuildResultColumnsParams): ColumnDef<OptimizerResultRow>[] {
    return [
        {
            header: '',
            id: 'select',
            size: 50,
            enableSorting: false,
            cell: ({ row }) => (
                <FormCheckbox
                    checked={bundleSelectedResultIds.has(row.original.id)}
                    id={`opt-result-select-${row.original.id}`}
                    onChange={() => toggleBundleSelectedResultId(row.original.id)}
                />
            )
        },
        {
            header: '#',
            id: 'pos',
            size: 60,
            enableSorting: false,
            cell: ({ row }) => <span className="text-zinc-400">{displayIndexByResultId.get(row.original.id)?.toLocaleString() ?? '-'}</span>
        },
        {
            header: 'Result ID',
            accessorKey: 'id',
            size: 90,
            cell: ({ row }) => <span className="font-mono text-zinc-300">{row.original.id}</span>
        },
        {
            header: 'Promotion',
            id: 'promotion',
            size: 110,
            enableSorting: false,
            cell: ({ row }) =>
                row.original.promotedScreenerRuleId !== null ? (
                    <Badge variant="success">Rule {row.original.promotedScreenerRuleId}</Badge>
                ) : (
                    <span className="text-zinc-500">-</span>
                )
        },
        {
            header: 'Score',
            accessorKey: 'selectionScore',
            size: 90,
            cell: ({ row }) => <span className="text-white">{formatDecimal(row.original.selectionScore, 4)}</span>
        },
        {
            header: 'Robust',
            id: 'auditScore',
            size: 115,
            enableSorting: false,
            cell: ({ row }) => {
                const audit = auditCandidateByResultId.get(row.original.id);

                if (!audit) {
                    return <span className="text-zinc-500 text-xs">{auditPackPending ? 'Auditing...' : '-'}</span>;
                }

                return (
                    <div>
                        <Badge variant={badgeVariantForAuditGrade(audit.robustness.grade)}>
                            {audit.robustness.score} {audit.robustness.grade}
                        </Badge>
                        {audit.robustness.flags.length > 0 && (
                            <div className="text-zinc-500 text-[10px] mt-0.5">{audit.robustness.flags.length} flag(s)</div>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Rule',
            accessorKey: 'computedRuleSummary',
            size: 280,
            enableSorting: false,
            cell: ({ row }) => (
                <span className="text-white text-xs break-words" title={row.original.computedRuleSummary}>
                    {row.original.computedRuleSummary.length > 80
                        ? `${row.original.computedRuleSummary.slice(0, 80)}…`
                        : row.original.computedRuleSummary}
                </span>
            )
        },
        {
            header: 'Avg Trade (raw)',
            accessorKey: 'avgProfit',
            size: 130,
            cell: ({ row }) => {
                const avg = toNumber(row.original.avgProfit);
                const baseline = toNumber(row.original.baselineAvgProfit);
                return (
                    <div>
                        <span className={avg >= 0 ? 'text-green-400' : 'text-red-400'}>{formatSignedPct(avg)}</span>
                        <div className="text-zinc-500 text-[10px]">expected: {formatSignedPct(baseline)}</div>
                    </div>
                );
            }
        },
        {
            // Exposure-matched mean per-entry edge vs the random-entry stock baselines.
            // Null = not computed (row predates the metric / no covered baselines) — never 0.
            header: 'Timing Edge',
            accessorKey: 'timingEdge',
            size: 110,
            cell: ({ row }) => {
                const covered = row.original.timingEdgeCoveredCount;

                if (row.original.timingEdge === null) {
                    return (
                        <div>
                            <span className="text-zinc-500">-</span>
                            {covered !== null && (
                                <div className="text-zinc-500 text-[10px]">
                                    {covered.toLocaleString()}/{row.original.tradeCount.toLocaleString()} covered
                                </div>
                            )}
                        </div>
                    );
                }

                return (
                    <div>
                        <span className={profitColorClass(row.original.timingEdge)}>
                            {formatSignedPct(toNumber(row.original.timingEdge))}
                        </span>
                        <div className="text-zinc-500 text-[10px]">
                            {(covered ?? 0).toLocaleString()}/{row.original.tradeCount.toLocaleString()} covered
                        </div>
                    </div>
                );
            }
        },
        {
            header: RAW_RETURN_SUM_LABEL,
            accessorKey: 'totalProfit',
            size: 140,
            cell: ({ row }) => {
                const rawReturnSum = row.original.totalProfit;
                const baselineRawReturnSum = toNumber(row.original.baselineAvgProfit) * row.original.tradeCount;

                return (
                    <div>
                        <span className={profitColorClass(rawReturnSum)}>{formatSignedPct(rawReturnSum)}</span>
                        <div className="text-zinc-500 text-[10px]">expected: {formatSignedPct(baselineRawReturnSum)}</div>
                    </div>
                );
            }
        },
        {
            header: selectedJobUsesOptionEv ? 'EV Win' : 'Win Rate',
            accessorKey: 'winRate',
            size: 80,
            cell: ({ row }) => <span className="text-white">{formatPct01(row.original.winRate, 1)}</span>
        },
        ...(selectedJobUsesOptionMetric
            ? ([
                  {
                      header: 'Executed Opt Win',
                      accessorKey: 'optionWinRate',
                      size: 120,
                      cell: ({ row }) => <span className="text-white">{formatNullablePct01(row.original.optionWinRate, 1)}</span>
                  },
                  {
                      header: 'Opt Exec',
                      accessorKey: 'optionExecutionRate',
                      size: 95,
                      cell: ({ row }) => <span className="text-white">{formatNullablePct01(row.original.optionExecutionRate, 1)}</span>
                  },
                  {
                      header: 'Opt Trades',
                      accessorKey: 'optionTradeCount',
                      size: 95,
                      cell: ({ row }) => <span className="text-white">{row.original.optionTradeCount?.toLocaleString() ?? '-'}</span>
                  },
                  {
                      header: 'Avg Exec Opt',
                      accessorKey: 'avgOptionProfit',
                      size: 120,
                      cell: ({ row }) => {
                          if (row.original.avgOptionProfit === null) return <span className="text-zinc-500">-</span>;
                          return (
                              <div>
                                  <span className={profitColorClass(row.original.avgOptionProfit)}>
                                      {formatSignedPct(toNumber(row.original.avgOptionProfit))}
                                  </span>
                                  {row.original.optionBaselineAvgProfit !== null && (
                                      <div className="text-zinc-500 text-[10px]">
                                          base {formatSignedPct(toNumber(row.original.optionBaselineAvgProfit))}
                                      </div>
                                  )}
                              </div>
                          );
                      }
                  },
                  {
                      header: 'Median Exec Opt',
                      accessorKey: 'medianOptionProfit',
                      size: 130,
                      cell: ({ row }) => {
                          if (row.original.medianOptionProfit === null) return <span className="text-zinc-500">-</span>;
                          return (
                              <span className={profitColorClass(row.original.medianOptionProfit)}>
                                  {formatSignedPct(toNumber(row.original.medianOptionProfit))}
                              </span>
                          );
                      }
                  },
                  {
                      // Exposure-matched mean per-entry edge vs the random-entry option-archetype
                      // baselines (no side mirroring — the archetype encodes side). Null = not computed.
                      header: 'Opt Timing Edge',
                      accessorKey: 'optionTimingEdge',
                      size: 120,
                      cell: ({ row }) => {
                          const covered = row.original.optionTimingEdgeCoveredCount;
                          const optionTrades = row.original.optionTradeCount;

                          if (row.original.optionTimingEdge === null) {
                              return (
                                  <div>
                                      <span className="text-zinc-500">-</span>
                                      {covered !== null && (
                                          <div className="text-zinc-500 text-[10px]">
                                              {covered.toLocaleString()}/{optionTrades?.toLocaleString() ?? '-'} covered
                                          </div>
                                      )}
                                  </div>
                              );
                          }

                          return (
                              <div>
                                  <span className={profitColorClass(row.original.optionTimingEdge)}>
                                      {formatSignedPct(toNumber(row.original.optionTimingEdge))}
                                  </span>
                                  <div className="text-zinc-500 text-[10px]">
                                      {(covered ?? 0).toLocaleString()}/{optionTrades?.toLocaleString() ?? '-'} covered
                                  </div>
                              </div>
                          );
                      }
                  }
              ] satisfies ColumnDef<OptimizerResultRow>[])
            : []),
        {
            header: 'Trades',
            accessorKey: 'tradeCount',
            size: 110,
            cell: ({ row }) => (
                <div>
                    <span className="text-white">{row.original.tradeCount.toLocaleString()}</span>
                    <div className="text-zinc-500 text-[10px]">{formatPct01(row.original.coverage, 1)} coverage</div>
                </div>
            )
        },
        {
            header: 'Profit Factor',
            accessorKey: 'profitFactor',
            size: 100,
            cell: ({ row }) => {
                if (row.original.profitFactor === null) return <span className="text-zinc-500">-</span>;
                const pf = row.original.profitFactor;
                const color = pf >= 1.5 ? 'text-green-400' : pf >= 1.0 ? 'text-yellow-400' : 'text-red-400';
                return <span className={color}>{pf.toFixed(2)}x</span>;
            }
        },
        {
            header: 'Validated',
            id: 'validated',
            size: 110,
            enableSorting: false,
            cell: ({ row }) => {
                const strength = computeValidationStrength(row.original);
                const label = strength.charAt(0).toUpperCase() + strength.slice(1);
                const hoLift = row.original.holdoutLift;
                const hoTrades = row.original.holdoutTradeCount;
                return (
                    <div>
                        <Badge
                            className="cursor-pointer"
                            variant={validationBadgeVariant(strength)}
                            onClick={() => onShowFolds(row.original.id)}
                        >
                            {label}
                        </Badge>
                        {(hoLift !== null || hoTrades !== null) && (
                            <div className="text-zinc-500 text-[10px] mt-0.5">
                                HO: {hoLift !== null ? formatSignedPct(toNumber(hoLift), 1) : '-'}
                                {hoTrades !== null ? ` (${hoTrades.toLocaleString()})` : ''}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Cond',
            accessorKey: 'conditionCount',
            size: 60
        },
        {
            header: 'Actions',
            id: 'actions',
            size: 230,
            enableSorting: false,
            cell: ({ row }) => {
                const replayPending = replayConfirmationPendingResultId === row.original.id;

                return (
                    <div className="flex gap-2">
                        <Button size="sm" variant="secondary" onClick={() => onShowRuleJson(row.original)}>
                            Rule
                        </Button>
                        {selectedJobOutcomeMode === 'strategy_exit' && row.original.promotedScreenerRuleId === null && (
                            <Button disabled={replayPending} size="sm" variant="secondary" onClick={() => onConfirmReplay(row.original)}>
                                {replayPending ? 'Replay…' : 'Replay'}
                            </Button>
                        )}
                        <Button
                            disabled={row.original.promotedScreenerRuleId !== null}
                            size="sm"
                            variant={row.original.promotedScreenerRuleId !== null ? 'secondary' : 'primary'}
                            onClick={() => openPromoteModal(row.original)}
                        >
                            {row.original.promotedScreenerRuleId !== null ? 'Promoted' : 'Promote'}
                        </Button>
                    </div>
                );
            }
        }
    ];
}
